/**
 * Sinal de saude de um funil e o "proximo passo" derivado dos numeros.
 *
 * Decisoes nao obvias:
 *  1. A saude e um DIAGNOSTICO com motivo escrito, nunca so uma cor: o
 *     `Badge` recebe rotulo e a frase diz de onde veio ("taxa caiu de 21%
 *     para 17%"). Cor sozinha nao comunica estado (GUIA-PAINEL, secao 6).
 *  2. Os limiares sao por funil: expirados recuperam mais que carrinho
 *     (quem ja pagou uma vez volta mais), entao a mesma taxa que e saudavel
 *     num e atencao no outro. Sao PONTOS DE PARTIDA da previa; quando houver
 *     historico real, viram configuracao por empresa.
 *  3. Queda de 3 pontos ou mais contra o periodo anterior REBAIXA um nivel,
 *     mesmo com taxa absoluta boa: o lojista precisa saber que estava melhor.
 *  4. O proximo passo e UM so, o mais urgente, com o numero que o justifica.
 *     Lista de cinco sugestoes vira ruido; uma frase acionavel vira habito.
 *  5. Tudo parte do fato ocorrido: nenhum passo sugere "lembrar antes de
 *     vencer" ou "cobrar antes do abandono" -- sem o fato nao existe funil.
 */

import type { BadgeTone } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import type { FunnelKind } from '../../../types';
import type { FunnelSummary } from '../types';
import { caiuEmPontos } from './comparacao';

export type Saude = 'saudavel' | 'atencao' | 'critico' | 'pausado' | 'sem_dado';

export interface ProximoPasso {
  readonly texto: string;
  /** Rotulo do botao; `null` quando a acao esta na propria linha (ex.: o seletor de responsavel). */
  readonly rotulo: string | null;
  readonly rota: string | null;
}

export interface Diagnostico {
  readonly saude: Saude;
  readonly rotulo: string;
  readonly tone: BadgeTone;
  readonly motivo: string;
  readonly proximoPasso: ProximoPasso;
}

interface Limiares {
  readonly saudavelBps: number;
  readonly atencaoBps: number;
}

const LIMIARES: Readonly<Record<FunnelKind, Limiares>> = {
  carrinho_abandonado: { saudavelBps: 1_800, atencaoBps: 1_200 },
  expirados: { saudavelBps: 2_500, atencaoBps: 1_500 },
};

const QUEDA_RELEVANTE_BPS = 300;

const ROTULO: Readonly<Record<Saude, { readonly rotulo: string; readonly tone: BadgeTone }>> = {
  saudavel: { rotulo: 'Saudável', tone: 'good' },
  atencao: { rotulo: 'Atenção', tone: 'warning' },
  critico: { rotulo: 'Crítico', tone: 'critical' },
  pausado: { rotulo: 'Pausado', tone: 'neutral' },
  sem_dado: { rotulo: 'Sem entradas', tone: 'neutral' },
};

const ROTA: Readonly<Record<FunnelKind, string>> = {
  carrinho_abandonado: '/funis/carrinho',
  expirados: '/funis/expirados',
};

function rebaixar(saude: Saude): Saude {
  if (saude === 'saudavel') {
    return 'atencao';
  }
  if (saude === 'atencao') {
    return 'critico';
  }
  return saude;
}

function nivelPelaTaxa(kind: FunnelKind, taxaBps: number): Saude {
  const limiar = LIMIARES[kind];
  if (taxaBps >= limiar.saudavelBps) {
    return 'saudavel';
  }
  if (taxaBps >= limiar.atencaoBps) {
    return 'atencao';
  }
  return 'critico';
}

/** Verbo do fato gerador, para as frases nao dizerem "abandonaram" no funil de expirados. */
function fato(kind: FunnelKind): { readonly entraram: string; readonly voltaram: string; readonly gatilho: string } {
  return kind === 'expirados'
    ? { entraram: 'venceram', voltaram: 'voltaram a pagar', gatilho: 'no dia seguinte ao vencimento' }
    : { entraram: 'abandonaram', voltaram: 'voltaram e pagaram', gatilho: '30 min depois do abandono' };
}

export function diagnosticar(resumo: FunnelSummary | null, kind: FunnelKind, responsavelId: string | null): Diagnostico {
  const f = fato(kind);
  const rota = ROTA[kind];

  if (resumo !== null && !resumo.active) {
    return {
      saude: 'pausado',
      ...ROTULO.pausado,
      motivo: 'O disparo está desligado: ninguém que entrou está sendo contatado.',
      proximoPasso: { texto: 'Religar o funil para o motor voltar a agir sobre quem já saiu.', rotulo: 'Abrir configurações', rota: '/configuracoes' },
    };
  }

  if (resumo === null || resumo.enteredCount === 0) {
    return {
      saude: 'sem_dado',
      ...ROTULO.sem_dado,
      motivo: `Ninguém ${f.entraram} no período.`,
      proximoPasso: { texto: `Nada a fazer agora: o motor age sozinho ${f.gatilho}.`, rotulo: null, rota: null },
    };
  }

  const anteriorBps = resumo.previous?.recoveryRateBps ?? null;
  const caiu = caiuEmPontos(resumo.recoveryRateBps, anteriorBps, QUEDA_RELEVANTE_BPS);
  let saude = nivelPelaTaxa(kind, resumo.recoveryRateBps);
  if (caiu) {
    saude = rebaixar(saude);
  }

  const taxa = formatPercentFromBps(resumo.recoveryRateBps);
  const motivo = caiu
    ? `Taxa de ${taxa}, abaixo dos ${formatPercentFromBps(anteriorBps ?? 0)} do período anterior.`
    : saude === 'saudavel'
      ? `Taxa de ${taxa}: ${formatCount(resumo.recoveredCount)} de ${formatCount(resumo.enteredCount)} que ${f.entraram} ${f.voltaram}.`
      : saude === 'atencao'
        ? `Taxa de ${taxa}, abaixo do que este funil costuma entregar.`
        : `Taxa de ${taxa}: a régua está deixando passar quem ${f.entraram}.`;

  return { saude, ...ROTULO[saude], motivo, proximoPasso: proximoPasso(resumo, kind, responsavelId, caiu, rota) };
}

function proximoPasso(resumo: FunnelSummary, kind: FunnelKind, responsavelId: string | null, caiu: boolean, rota: string): ProximoPasso {
  const f = fato(kind);

  if (caiu && resumo.previous !== null) {
    return {
      texto: `Revisar os toques: a taxa caiu de ${formatPercentFromBps(resumo.previous.recoveryRateBps)} para ${formatPercentFromBps(resumo.recoveryRateBps)}. Compare oferta a oferta para achar onde a queda começou.`,
      rotulo: 'Abrir a régua',
      rota,
    };
  }

  if (responsavelId === null) {
    return {
      texto: 'Designar um responsável: quem responde à mensagem cai na caixa do comercial, e sem dono a resposta esfria antes dos 7 dias.',
      rotulo: null,
      rota: null,
    };
  }

  if (resumo.openCount > 0 && resumo.openAmountCents > 0) {
    return {
      texto: `${formatBRLFromCents(resumo.openAmountCents)} em ${formatCount(resumo.openCount)} ${resumo.openCount === 1 ? 'lead' : 'leads'} que ${f.entraram} e ainda dão para recuperar. Os de maior valor merecem um contato humano.`,
      rotulo: 'Ver quem está em aberto',
      rota,
    };
  }

  return {
    texto: 'Régua no ritmo. O próximo salto costuma estar na oferta com pior taxa: compare por oferta.',
    rotulo: 'Comparar por oferta',
    rota,
  };
}
